import {
  forwardRef,
  useRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { motion, useReducedMotion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { springs } from "@andrum/design-tokens";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-full text-base font-semibold tracking-[-0.01em]",
    "min-h-12 px-6",
    "transition-[background-color,box-shadow,transform,opacity] duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "disabled:pointer-events-none disabled:opacity-50",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-primary text-primary-foreground shadow-[0_1px_0_rgb(255_255_255_/_0.12)_inset,0_8px_20px_rgb(95_77_140_/_0.28)] hover:shadow-[0_1px_0_rgb(255_255_255_/_0.14)_inset,0_12px_26px_rgb(95_77_140_/_0.34)]",
        secondary:
          "bg-secondary text-secondary-foreground shadow-[0_6px_16px_rgb(61_139_110_/_0.22)]",
        soft: "bg-primary-soft text-primary shadow-none",
        ghost: "bg-transparent text-foreground-muted hover:bg-surface-muted/80 shadow-none",
        outline:
          "border border-border/80 bg-surface-raised/90 text-foreground shadow-none",
        danger: "bg-danger text-white shadow-[0_6px_16px_rgb(180_35_24_/_0.22)]",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      fullWidth: false,
    },
  }
);

export type ButtonProps = {
  children: ReactNode;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  fullWidth?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  id?: string;
  form?: string;
  name?: string;
  "aria-label"?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "primary",
      fullWidth = false,
      loading = false,
      loadingLabel = "Laddar",
      disabled = false,
      children,
      onClick,
      type = "button",
      id,
      form,
      name,
      "aria-label": ariaLabel,
    },
    ref
  ) {
    const reducedMotion = useReducedMotion();
    const lockRef = useRef(false);
    const isDisabled = disabled || loading;

    const handleClick: ButtonHTMLAttributes<HTMLButtonElement>["onClick"] = (
      event
    ) => {
      if (isDisabled || lockRef.current) {
        event.preventDefault();
        return;
      }
      lockRef.current = true;
      try {
        onClick?.(event);
      } finally {
        window.setTimeout(() => {
          lockRef.current = false;
        }, 400);
      }
    };

    const allowPressMotion = !reducedMotion && !isDisabled;

    return (
      <motion.button
        ref={ref}
        type={type}
        id={id}
        form={form}
        name={name}
        aria-label={ariaLabel}
        className={cn(buttonVariants({ variant, fullWidth }), className)}
        disabled={isDisabled}
        aria-busy={loading ? true : undefined}
        {...(allowPressMotion ? { whileTap: { scale: 0.975 } } : {})}
        transition={springs.responsive}
        onClick={handleClick}
      >
        {loading ? (
          <>
            <Spinner size="sm" label={loadingLabel} />
            <span className="opacity-90">{children}</span>
          </>
        ) : (
          children
        )}
      </motion.button>
    );
  }
);

export { buttonVariants };
